import React, { useRef, useEffect, useState } from "react";
import styled from "@emotion/styled";
import useDashboard from "../../../hooks/useDashboard";
import useAuth from "../../../hooks/useAuth";
import {
  Box,
  Card,
  Typography,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  MenuItem,
  Select,
  FormControl,
  Badge as MuiBadge,
} from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Download, TrendingUp, MoreVertical, ChevronDown } from "react-feather";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

const GlassCard = styled(Card)`
  background: ${(props) =>
    props.theme.palette.mode === "dark"
      ? props.theme.palette.background.paper
      : "rgba(255, 255, 255, 0.9)"};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: ${(props) =>
    props.theme.palette.mode === "dark"
      ? "0 10px 40px -10px rgba(0, 0, 0, 0.4)"
      : "0 10px 40px -10px rgba(0, 0, 0, 0.1)"};
  border: ${(props) =>
    props.theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(255, 255, 255, 0.3)"};
  overflow: hidden;
`;

const StatBadge = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 30px;
  background: rgba(34, 197, 94, 0.1);
  color: #16a34a;
  font-size: 12px;
  font-weight: 700;
`;

const HeaderSection = ({ period, setPeriod, range, setRange, totalRevenue, avgRevenue }) => (
  <Box sx={{ p: 2, pb: 0 }}>
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.25 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "text.primary", fontSize: "13px" }}
          >
            Revenue Collected in Last Month
          </Typography>
          <StatBadge sx={{ py: 0.1, px: 0.8, fontSize: "10px" }}>
            <TrendingUp size={10} />
            {totalRevenue > 0 ? "+12.5%" : "0%"}
          </StatBadge>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 500, fontSize: "11px" }}
        >
          Last 30 Days Performance
        </Typography>
      </Grid>
      <Grid item>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={(e, v) => v && setPeriod(v)}
            size="small"
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
              p: 0.3,
              borderRadius: "8px",
              border: "none",
              "& .MuiToggleButton-root": {
                border: "none",
                borderRadius: "5px !important",
                px: 1,
                py: 0.2,
                fontSize: "11px",
                textTransform: "none",
                fontWeight: 600,
                color: (theme) =>
                  theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                minWidth: "auto",
                "&.Mui-selected": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? theme.palette.primary.main : "#ffffff",
                  color: "#ffffff",
                },
              },
            }}
          >
            <ToggleButton value="daily">D</ToggleButton>
            <ToggleButton value="weekly">W</ToggleButton>
            <ToggleButton value="monthly">M</ToggleButton>
          </ToggleButtonGroup>

          <Select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            size="small"
            sx={{
              borderRadius: "8px",
              backgroundColor: (theme) =>
                theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f1f5f9",
              height: "28px",
              "& fieldset": { border: "none" },
              fontWeight: 600,
              fontSize: "11px",
              color: "text.primary",
              minWidth: "60px",
            }}
          >
            <MenuItem value={7}>7D</MenuItem>
            <MenuItem value={30}>30D</MenuItem>
            <MenuItem value={90}>90D</MenuItem>
          </Select>
        </Box>
      </Grid>
    </Grid>

    <Box sx={{ mt: 1.5, display: "flex", gap: 4 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "9px",
          }}
        >
          Total Revenue
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mt: 0 }}>
          ₹{totalRevenue.toLocaleString()}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "9px",
          }}
        >
          Avg per Day
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mt: 0 }}>
          ₹{avgRevenue.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const RevenueChart = () => {
  const [period, setPeriod] = useState("daily");
  const [range, setRange] = useState(30);
  const chartRef = useRef(null);
  const { user } = useAuth();
  const { getRevenueChartData, revenueChartData, isRevenueLoading } = useDashboard();

  const fetchData = () => {
    const filters = [];
    if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
      filters.push({
        fieldName: "tenantId",
        fieldValue: user.tenantId,
        op: "eq",
        dataType: "long",
        condition: "and",
        isJoin: false,
      });
    }

    // Add date range filters
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - range);

    filters.push({
      fieldName: "createdAt",
      fieldValue: startDate.toISOString().split("T")[0],
      op: "ge",
      dataType: "date",
      condition: "and",
    });

    filters.push({
      fieldName: "createdAt",
      fieldValue: endDate.toISOString().split("T")[0],
      op: "le",
      dataType: "date",
      condition: "and",
    });

    getRevenueChartData({
      filters,
      pagination: { pageNumber: 1, pageSize: 100 },
      sortFields: [{ fieldName: "createdAt", sortOrder: "ASC" }],
      period: period, // Assuming API might support period grouping
    });
  };

  useEffect(() => {
    fetchData();
  }, [user, range, period]);

  const labels =
    (revenueChartData?.length || 0) > 0
      ? revenueChartData.map((d) => {
          const date = new Date(d.createdAt);
          return `${date.getDate()} ${date.toLocaleString("default", { month: "short" })}`;
        })
      : Array.from({ length: range }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (range - 1 - i));
          return `${d.getDate()} ${d.toLocaleString("default", { month: "short" })}`;
        });

  const dataPoints =
    (revenueChartData?.length || 0) > 0
      ? revenueChartData.map((d) => d.totalAmount || 0)
      : Array(range).fill(0);

  const totalRevenue = dataPoints.reduce((a, b) => a + b, 0);
  const avgRevenue =
    (dataPoints?.length || 0) > 0 ? Math.round(totalRevenue / dataPoints.length) : 0;

  const chartData = {
    labels,
    datasets: [
      {
        fill: true,
        label: "Revenue",
        data: dataPoints,
        borderColor: "#22c55e",
        borderWidth: 2,
        pointRadius: (context) => {
          const value = context.raw;
          const max = Math.max(...dataPoints);
          return value === max && value > 0 ? 4 : 0;
        },
        pointHoverRadius: 6,
        pointBackgroundColor: "#22c55e",
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        pointHoverBackgroundColor: "#22c55e",
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
        tension: 0.4,
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return null;
          const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
          gradient.addColorStop(0, "rgba(34, 197, 94, 0)");
          gradient.addColorStop(1, "rgba(34, 197, 94, 0.15)");
          return gradient;
        },
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        padding: 8,
        borderRadius: 6,
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 11 },
        displayColors: false,
        callbacks: {
          label: (context) => `₹${context.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 9, weight: 600 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 5,
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.03)",
          drawBorder: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 9 },
          maxTicksLimit: 4,
          callback: (value) => `₹${value >= 1000 ? value / 1000 + "k" : value}`,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  return (
    <Grid item xs={12} md={6}>
      <GlassCard>
        <HeaderSection
          period={period}
          setPeriod={setPeriod}
          range={range}
          setRange={setRange}
          totalRevenue={totalRevenue}
          avgRevenue={avgRevenue}
        />
        <Box sx={{ height: 140, mt: 0.5, position: "relative" }}>
          {isRevenueLoading ? (
            <Box
              sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                Loading Data...
              </Typography>
            </Box>
          ) : (
            <Line data={chartData} options={options} ref={chartRef} />
          )}
        </Box>
      </GlassCard>
    </Grid>
  );
};

export default RevenueChart;
